import moment from "moment";

export { authorizedRequest } from "./utils.requests";
export { request } from "./utils.requests";
export { requestFormData } from "./utils.requests";
export { toastError } from "./utils.toasts";
export { toastSuccess } from "./utils.toasts";
export { toastWarning } from "./utils.toasts";
export { decodeToken } from "./utils.decodeToken";
export { decodeTokenWithParam } from "./utils.decodeToken";
export { text_truncate } from "./truncate.text";
export { calculatePageButtons } from "./calculate.pageRange";
export { triggerShare } from "./util.share";
export { groupInputs } from "./util.groupInputs";
export { capitalize } from "./util.capitalize";
export { printHtml } from "./util.printHtml";
export { findOption } from "./util.findOptionInList";
export { copyTextToClipboard } from "./util.copyToClipBoard";
export { exportToCSV } from "./util.exportToCSV";
export { axiosAuth } from "./axios.config";

export function convertTimestampDataToDayRange(data) {
  // Create an empty object to hold the counts for each date range
  const dateRanges = {};

  // Loop through each timestamp and convert it to a Date object
  for (const timestamp of data) {
    const date = new Date(timestamp);

    // Get the year, month, and day of the date
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // Create a key for the date range (e.g. "2022-03-27")
    const key = `${year}-${month.toString().padStart(2, "0")}-${day
      .toString()
      .padStart(2, "0")}`;

    // Increment the count for the date range
    if (dateRanges.hasOwnProperty(key)) {
      dateRanges[key]++;
    } else {
      dateRanges[key] = 1;
    }
  }

  // Convert the date ranges object into an array of objects
  const result = [];
  for (const key in dateRanges) {
    result.push({ dateRange: key, count: dateRanges[key] });
  }

  // Sort the result array by date range
  result.sort((a, b) => (a.dateRange > b.dateRange ? 1 : -1));

  return result;
}

export function convertTimestampDataToMonthRange(data) {
  // Create an empty object to hold the counts for each date range
  const dateRanges = {};

  // Loop through each timestamp and convert it to a Date object
  for (const timestamp of data) {
    const date = new Date(timestamp);

    // Get the year and month of the date
    const year = date.getFullYear();
    const month = date.getMonth() + 1;

    // Create a key for the date range (e.g. "2022-03")
    const key = `${year}-${month.toString().padStart(2, "0")}`;

    // Increment the count for the date range
    if (dateRanges.hasOwnProperty(key)) {
      dateRanges[key]++;
    } else {
      dateRanges[key] = 1;
    }
  }

  // Convert the date ranges object into an array of objects
  const result = [];
  for (const key in dateRanges) {
    result.push({ dateRange: key, count: dateRanges[key] });
  }

  // Sort the result array by date range
  result.sort((a, b) => (a.dateRange > b.dateRange ? 1 : -1));

  // Modify the dateRange property of the result objects to show only the month
  for (const obj of result) {
    const [year, month] = obj.dateRange.split("-");
    obj.dateRange = `${year}-${month}`;
  }

  // Return the modified result array
  return result;
}

export function getMonthlyData(viewsDates, saveDates, referDates) {
  // Combine the two sets of dates
  const allDates = viewsDates.concat(saveDates, referDates);

  // Create an object to hold the monthly data
  const monthlyData = {};

  // Loop through all the dates
  allDates.forEach((date) => {
    // Get the month and year for the date
    const month = new Date(date).getMonth();
    const year = new Date(date).getFullYear();

    // Create a key for the month and year if it doesn't already exist
    const key = `${year}-${month}`;
    if (!monthlyData[key]) {
      monthlyData[key] = {
        dateRange: `${year}-${month}`,
        views: 0,
        saves: 0,
        refers: 0,
      };
    }

    // Increment the views count if the date is in the viewsDates array
    if (viewsDates.includes(date)) {
      monthlyData[key].views += 1;
    }

    // Increment the saves count if the date is in the saveDates array
    if (saveDates.includes(date)) {
      monthlyData[key].saves += 1;
    }

    // Increment the refers count if the date is in the referDates array
    if (referDates.includes(date)) {
      monthlyData[key].refers += 1;
    }
  });

  // Convert the monthlyData object into an array of objects
  const monthlyDataArray = Object.values(monthlyData);

  // Return the monthly data array
  return monthlyDataArray;
}

export function getDailyData(viewsDates, saveDates, referDates) {
  // Combine all sets of dates into a single array without duplicates
  const allDates = [...new Set([...viewsDates, ...saveDates, ...referDates])];

  // Create an object to hold the daily data
  const dailyData = {};

  // Helper function to add or update the count for a specific category
  function updateCount(date, category) {
    const formattedDate = moment(date).format("DD MMM YYYY");
    if (!dailyData[formattedDate]) {
      dailyData[formattedDate] = {
        dateRange: formattedDate,
        views: 0,
        saves: 0,
        refers: 0,
      };
    }
    dailyData[formattedDate][category] += 1;
  }

  // Process each category separately to ensure unique counts
  viewsDates.forEach((date) => {
    if (allDates.includes(date)) updateCount(date, "views");
  });

  saveDates.forEach((date) => {
    if (allDates.includes(date)) updateCount(date, "saves");
  });

  referDates.forEach((date) => {
    if (allDates.includes(date)) updateCount(date, "refers");
  });

  // Convert the dailyData object into an array of objects
  return Object.values(dailyData);
}

export const getInitials = (name) => {
  return name
    .match(/(\b\S)?/g)
    .join("")
    .match(/(^\S|\S$)?/g)
    .join("");
};
